import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  HelpCircle, 
  Search, 
  BookOpen, 
  Users, 
  Video, 
  CreditCard, 
  MessageSquare,
  FileQuestion,
  Settings,
  Shield,
  Mail,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function HelpCenter() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const helpSections = {
    getting_started: {
      icon: BookOpen,
      title: "Getting Started",
      articles: [
        {
          title: "Welcome to Visainterviews.ai",
          content: `Visainterviews.ai is an AI-powered platform designed to help students prepare for university credibility interviews. 

Our system provides:
• Realistic mock interviews with AI-powered questions
• Proctoring features including face detection and eye contact monitoring
• Detailed performance analysis and scoring
• Video recordings for review

Whether you're an agent managing students or an organization conducting assessments, our platform streamlines the entire interview process.`
        },
        {
          title: "Account Types Explained",
          content: `There are several account types on our platform:

**Super Admin**: Full system access, manages all users, settings, and question banks.

**Organization**: Educational institutions or companies that conduct interviews. Can create agents, manage their own question banks, and allocate credits.

**Agent**: Recruitment agents or counselors who create interview applications for students. Can have sub-agents (partners).

**Student**: End users who take the interviews. They receive interview links via email.`
        },
        {
          title: "Dashboard Overview",
          content: `Your dashboard shows key metrics and quick actions:

• **Credits**: Your available interview credits (both personal and allocated)
• **Recent Activity**: Latest interviews and applications
• **Quick Stats**: Total applications, completed interviews, pass rates
• **Navigation**: Access all features from the sidebar menu`
        }
      ]
    },
    interviews: {
      icon: Video,
      title: "Managing Interviews",
      articles: [
        {
          title: "Creating Interview Applications",
          content: `To create an interview for a student:

1. Go to "Create Interviews" in the sidebar
2. Click "Create Application" or "Bulk Import"
3. Fill in the student details:
   - Full name
   - Email address
   - Select university (if applicable)
4. Choose interview settings
5. Click "Create"

The student will receive an email with their interview link automatically.`
        },
        {
          title: "Interview Flow for Students",
          content: `When a student accesses their interview:

1. **Verification**: Upload passport/ID and take a selfie for identity verification
2. **Consent**: Read and accept the interview terms
3. **Fullscreen Mode**: The interview runs in fullscreen to prevent distractions
4. **Questions**: Listen to each question (with TTS), then record video responses
5. **Completion**: Results are processed by AI and emailed to the student

The entire process is monitored with proctoring features.`
        },
        {
          title: "Viewing Interview Results",
          content: `After an interview is completed:

1. Go to "Interview Recordings" in the sidebar
2. Find the interview and click "View Details"
3. You'll see:
   - Overall AI score and pass/fail status
   - Individual question scores
   - Proctoring metrics (eye contact, face match, etc.)
   - Video recordings of each answer
   - Full transcript

Students also receive their results via email with a secure link to view their detailed report.`
        },
        {
          title: "Understanding Proctoring",
          content: `Our proctoring system monitors:

**Face Detection**: Ensures the student's face is visible throughout

**Face Matching**: Compares the student to their uploaded ID photo

**Eye Contact**: Tracks whether the student is looking at the camera

**Liveness Detection**: Monitors for natural head movements

Low scores in these areas may indicate potential issues that should be reviewed manually.`
        }
      ]
    },
    questions: {
      icon: FileQuestion,
      title: "Question Bank",
      articles: [
        {
          title: "Managing Questions",
          content: `The Question Bank stores all interview questions.

**For Super Admins**:
- Create and manage generic questions for all universities
- Import/export questions via JSON

**For Organizations**:
- Create your own question bank
- Enable/disable your custom questions
- Import questions from JSON files

Each question has:
• Category (for diverse topic coverage)
• Keywords (for AI scoring)
• Expected points (ideal answer elements)
• Listening and answering time settings`
        },
        {
          title: "Question Categories",
          content: `Questions are organized into 17 categories:

• Motivation & Reasons
• Education Quality
• Personal & Cultural Expectations
• UK Study Choice
• Campus Choice
• Course Understanding
• University Choice
• Sector Consideration
• Accommodation
• Funding
• Academic History
• Work Plans
• Post-Study Plans
• Career Goals
• Visa Refusal
• Dependants
• Fixed Question 2

The system automatically selects questions from different categories to ensure comprehensive interviews.`
        },
        {
          title: "Importing Questions",
          content: `To bulk import questions:

1. Download the sample JSON template
2. Fill in your questions following the format
3. Include the category field for each question
4. Upload the JSON file

Sample format:
{
  "questions": [
    {
      "title": "Question Title",
      "question_text": "Your question here?",
      "category": "Motivation & Reasons",
      "keywords": ["keyword1", "keyword2"],
      "expected_points": ["Point 1", "Point 2"],
      "listening_time": 30,
      "answering_time": 90,
      "status": "active"
    }
  ]
}`
        }
      ]
    },
    credits: {
      icon: CreditCard,
      title: "Credits & Billing",
      articles: [
        {
          title: "Understanding Credits",
          content: `Credits are used to conduct interviews. Each interview consumes one credit.

**Personal Credits**: Credits you purchase directly
**Allocated Credits**: Credits given to you by an organization or parent agent

Your total available credits = Personal + Allocated

You can see your credit balance in the top navigation bar.`
        },
        {
          title: "Purchasing Credits",
          content: `To buy more credits:

1. Click "Buy Credits" in the top navigation
2. Select a credit package or enter custom amount
3. Choose payment method (Stripe or PayPal)
4. Complete the payment

Credits are added to your account immediately after successful payment.`
        },
        {
          title: "Allocating Credits (Organizations)",
          content: `Organizations can distribute credits to their agents:

1. Go to "Agents" in the sidebar
2. Find the agent you want to allocate credits to
3. Click "Allocate Credits"
4. Enter the amount
5. Confirm

Allocated credits can be used by the agent but remain part of your organization's pool.`
        }
      ]
    },
    agents: {
      icon: Users,
      title: "Agent Management",
      articles: [
        {
          title: "Creating Agents",
          content: `Organizations can create agents:

1. Go to "Agents" in the sidebar
2. Click "Add Agent"
3. Fill in agent details:
   - Business name
   - Representative name
   - Email
   - Phone (optional)
4. Allocate initial credits (optional)
5. Click "Create"

The agent will receive a welcome email with login credentials.`
        },
        {
          title: "Partner Agents (Sub-agents)",
          content: `Agents can create their own partner agents:

1. Go to "Partner Agents" in the sidebar
2. Click "Add Partner"
3. Fill in partner details
4. Allocate credits from your pool
5. Click "Create"

Partner agents can create interviews and their activity counts toward your overall metrics.`
        },
        {
          title: "Viewing Agent Activity",
          content: `To monitor agent performance:

1. Go to "Agents" in the sidebar
2. View the agent list with key metrics:
   - Total applications
   - Completed interviews
   - Credits remaining

Click on an agent to see their detailed activity and manage their access.`
        }
      ]
    },
    support: {
      icon: MessageSquare,
      title: "Getting Help",
      articles: [
        {
          title: "Creating a Support Ticket",
          content: `If you need assistance:

1. Go to "Support" in the sidebar
2. Click "New Ticket"
3. Enter a subject line
4. Select priority:
   - Low: General inquiry
   - Medium: Need assistance
   - High: Important issue
   - Urgent: Critical problem
5. Describe your issue in detail
6. Submit

Our support team will respond via the ticket system and email.`
        },
        {
          title: "Tracking Your Tickets",
          content: `To check your ticket status:

1. Go to "Support" in the sidebar
2. View all your tickets
3. Filter by status (Open, In Progress, Resolved, Closed)
4. Click on a ticket to view the conversation
5. Add replies to continue the discussion

You'll receive email notifications when there's a response.`
        }
      ]
    }
  };

  const filteredSections = Object.entries(helpSections).reduce((acc, [key, section]) => {
    if (!searchQuery) {
      acc[key] = section;
      return acc;
    }
    
    const filteredArticles = section.articles.filter(
      article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (filteredArticles.length > 0) {
      acc[key] = { ...section, articles: filteredArticles };
    }
    
    return acc;
  }, {});

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-lime-500" />
              Help Center
            </h2>
            <p className="text-muted-foreground">Learn how to use Visainterviews.ai</p>
          </div>
          <Link to={user?.role === 'super_admin' ? '/admin/support' : '/support'}>
            <Button variant="outline">
              <MessageSquare className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(helpSections).map(([key, section]) => {
            const Icon = section.icon;
            return (
              <Card 
                key={key} 
                className="cursor-pointer hover:border-lime-500 transition-colors"
                onClick={() => document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' })}
              >
                <CardContent className="pt-4 text-center">
                  <Icon className="w-8 h-8 mx-auto text-lime-500 mb-2" />
                  <p className="text-sm font-medium">{section.title}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Sections */}
        {Object.entries(filteredSections).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No articles found matching "{searchQuery}"</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(filteredSections).map(([key, section]) => {
            const Icon = section.icon;
            return (
              <Card key={key} id={key}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-lime-500" />
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {section.articles.map((article, index) => (
                      <AccordionItem key={index} value={`${key}-${index}`}>
                        <AccordionTrigger className="text-left">
                          {article.title}
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="prose prose-sm max-w-none">
                            {article.content.split('\n').map((line, i) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return <p key={i} className="font-bold mt-4">{line.replace(/\*\*/g, '')}</p>;
                              }
                              if (line.startsWith('•')) {
                                return <p key={i} className="ml-4">{line}</p>;
                              }
                              if (line.trim() === '') {
                                return <br key={i} />;
                              }
                              return <p key={i} className="whitespace-pre-wrap">{line}</p>;
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* Contact Card */}
        <Card className="bg-gradient-to-r from-lime-50 to-green-50 border-lime-200">
          <CardContent className="py-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Still need help?</h3>
            <p className="text-muted-foreground mb-4">
              Our support team is ready to assist you with any questions.
            </p>
            <Link to={user?.role === 'super_admin' ? '/admin/support' : '/support'}>
              <Button className="bg-lime-500 hover:bg-lime-600">
                <MessageSquare className="w-4 h-4 mr-2" />
                Create Support Ticket
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
